import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Activity, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation, formatCount } from '@/i18n';
import { apiClient, ProviderVolumeResponse } from '@/utils/apiClient';

interface ProviderVolumeChartProps {
  className?: string;
}

const PROVIDER_COLORS: Record<string, { bar: string; badge: string }> = {
  gigachat:  { bar: 'bg-blue-400 dark:bg-blue-500',    badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
  openai:    { bar: 'bg-green-400 dark:bg-green-500',   badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
  anthropic: { bar: 'bg-orange-400 dark:bg-orange-500',  badge: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' },
  ollama:    { bar: 'bg-purple-400 dark:bg-purple-500',  badge: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' },
  unknown:   { bar: 'bg-gray-400 dark:bg-gray-500',     badge: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300' },
};

const getProviderColor = (provider: string) =>
  PROVIDER_COLORS[provider.toLowerCase()] ?? PROVIDER_COLORS.unknown;

// The volume query groups the whole audit_log; on a busy server it can take
// longer than every other widget. Give it a deadline so the card settles on
// an error with a retry instead of staying on "Loading..." forever (F-8a).
const REQUEST_TIMEOUT_MS = 20_000;

const EMPTY_VOLUME: ProviderVolumeResponse = { labels: [], series: {}, total_by_provider: {} };

export const ProviderVolumeChart: React.FC<ProviderVolumeChartProps> = ({ className }) => {
  const { t, language } = useTranslation();
  const at = (t as any).analytics;
  const [data, setData] = useState<ProviderVolumeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [days, setDays] = useState(7);
  // Controller of the request currently in flight; superseded or unmounted
  // requests are aborted and their result ignored.
  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const deadline = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getVolumeByProvider(days, controller.signal);
      if (abortRef.current !== controller) return;
      // A malformed or empty body renders the empty state, not the spinner.
      setData(result && typeof result === 'object' ? result : EMPTY_VOLUME);
    } catch (err) {
      if (abortRef.current !== controller) return;
      const timedOut = controller.signal.aborted;
      setError(
        timedOut
          ? (at?.requestTimedOut ?? 'The request timed out')
          : (err instanceof Error ? err.message : (at?.fetchError ?? 'Failed to load'))
      );
    } finally {
      clearTimeout(deadline);
      if (abortRef.current === controller) setLoading(false);
    }
  }, [days, at?.fetchError, at?.requestTimedOut]);

  useEffect(() => {
    fetchData();
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">{at?.loading ?? 'Loading...'}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm text-red-500 dark:text-red-400">{error}</span>
          <button onClick={fetchData} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
            {at?.refresh ?? 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  const providers = Object.keys(data?.total_by_provider ?? {});
  const totalQueries = Object.values(data?.total_by_provider ?? {}).reduce((a, b) => a + b, 0);
  const labels = data?.labels ?? [];
  const series = data?.series ?? {};

  const counts = t.analytics.counts;

  // Find max stacked value across all labels (for bar height scaling)
  const maxStacked = labels.length > 0
    ? Math.max(...labels.map((_, i) =>
        providers.reduce((sum, p) => sum + (series[p]?.[i] ?? 0), 0)
      ), 1)
    : 1;

  return (
    <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700', className)}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
            <Activity className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {at?.providerVolume ?? 'Provider Volume'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatCount(language, totalQueries, counts.queries)} · {formatCount(language, providers.length, counts.providers)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-gray-700 pt-4">
          {/* Period selector */}
          <div className="flex gap-2">
            {[7, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={clsx(
                  'px-2.5 py-1 text-xs rounded-md transition-colors',
                  days === d
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                )}
              >
                {d === 7 ? (at?.days7 ?? '7 days') : (at?.days30 ?? '30 days')}
              </button>
            ))}
          </div>

          {providers.length === 0 || labels.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">
              {at?.noQueriesLogged ?? 'No queries logged yet'}
            </p>
          ) : (
            <>
              {/* Provider badges / legend */}
              <div className="flex flex-wrap gap-2">
                {providers.map((prov) => {
                  const color = getProviderColor(prov);
                  const count = data?.total_by_provider[prov] ?? 0;
                  return (
                    <span
                      key={prov}
                      className={clsx('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium', color.badge)}
                    >
                      <span className={clsx('w-1.5 h-1.5 rounded-full', color.bar)} />
                      {prov} ({count})
                    </span>
                  );
                })}
              </div>

              {/* Stacked bar chart (CSS-only) */}
              <div className="flex items-end gap-px h-20">
                {labels.map((label, i) => {
                  const stacked = providers.reduce((sum, p) => sum + (series[p]?.[i] ?? 0), 0);
                  const heightPct = Math.max((stacked / maxStacked) * 100, 4);
                  return (
                    <div
                      key={label}
                      className="flex-1 flex flex-col-reverse rounded-t-sm overflow-hidden"
                      style={{ height: `${heightPct}%` }}
                      title={`${label}: ${stacked}`}
                    >
                      {providers.map((prov) => {
                        const val = series[prov]?.[i] ?? 0;
                        if (val === 0) return null;
                        const segPct = (val / stacked) * 100;
                        const color = getProviderColor(prov);
                        return (
                          <div
                            key={prov}
                            className={clsx('transition-all', color.bar)}
                            style={{ height: `${segPct}%` }}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Date labels (first and last) */}
              {labels.length > 1 && (
                <div className="flex justify-between text-[10px] text-gray-400 dark:text-gray-500">
                  <span>{labels[0]}</span>
                  <span>{labels[labels.length - 1]}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
